import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conversationsAPI } from '../services/api';
import { useMyTenants } from '../hooks/useMyTenants';
import { useAuthStore } from '../stores/authStore';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  sources?: any[];
}

interface Conversation {
  id: string;
  session_id: string;
  visitor_id?: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

export function ConversationsPage() {
  const queryClient = useQueryClient();
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);

  const { tenants } = useMyTenants();
  // [2026-03-24] 삭제는 슈퍼관리자만
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);
  const selectedTenantData = tenants.find((t: any) => t.id === selectedTenant);
  const apiKey = selectedTenantData?.api_key;

  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['conversations', selectedTenant],
    queryFn: () => conversationsAPI.list(apiKey!, 1, 100),
    enabled: !!apiKey,
  });

  const conversations: Conversation[] = conversationsData?.data?.items || [];

  const deleteMutation = useMutation({
    mutationFn: (convId: string) => conversationsAPI.delete(apiKey!, convId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations', selectedTenant] });
      setSelectedConversation(null);
    },
  });

  const fetchConversationDetail = async (convId: string) => {
    try {
      const response = await conversationsAPI.get(apiKey!, convId);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Failed to fetch conversation detail', error);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}일 전`;
    if (hours > 0) return `${hours}시간 전`;
    return '방금 전';
  };

  return (
    <div className="p-8 h-full">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">대화 기록</h1>
          <p className="text-gray-600">채팅봇 대화 기록을 확인합니다.</p>
        </div>
        <select
          value={selectedTenant}
          onChange={(e) => {
            setSelectedTenant(e.target.value);
            setSelectedConversation(null);
          }}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
        >
          <option value="">테넌트 선택</option>
          {tenants.map((tenant: any) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedTenant ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          테넌트를 선택하세요.
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          로딩 중...
        </div>
      ) : conversations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          대화 기록이 없습니다.
        </div>
      ) : (
        <div className="flex gap-6 h-[calc(100vh-220px)]">
          {/* Conversation List */}
          <div className="w-1/3 bg-white rounded-lg shadow overflow-hidden flex flex-col">
            <div className="p-4 border-b bg-gray-50">
              <h2 className="font-semibold">대화 목록</h2>
              <p className="text-sm text-gray-500">{conversations.length}개의 대화</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => fetchConversationDetail(conv.id)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition ${
                    selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {conv.session_id?.slice(0, 12)}...
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {conv.messages?.length || 0}개 메시지
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {formatTime(conv.updated_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Conversation Detail */}
          <div className="flex-1 bg-white rounded-lg shadow overflow-hidden flex flex-col">
            {selectedConversation ? (
              <>
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                  <div>
                    <h2 className="font-semibold">대화 상세</h2>
                    <p className="text-sm text-gray-500">
                      세션: {selectedConversation.session_id?.slice(0, 16)}...
                    </p>
                  </div>
                  {isSuperuser && (
                    <button
                      onClick={() => {
                        if (confirm('이 대화를 삭제하시겠습니까?')) {
                          deleteMutation.mutate(selectedConversation.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                      className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition"
                    >
                      삭제
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {selectedConversation.messages?.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        {msg.sources && msg.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-300">
                            <div className="text-xs text-gray-500 mb-1">참조 문서:</div>
                            {msg.sources.map((source: any, idx: number) => (
                              <div key={idx} className="text-xs text-gray-600">
                                - {source.document_title} ({Math.round((source.similarity_score || 0) * 100)}%)
                              </div>
                            ))}
                          </div>
                        )}
                        <div
                          className={`text-xs mt-1 ${
                            msg.role === 'user' ? 'text-blue-200' : 'text-gray-400'
                          }`}
                        >
                          {new Date(msg.created_at).toLocaleTimeString('ko-KR')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>
                      시작: {new Date(selectedConversation.created_at).toLocaleString('ko-KR')}
                    </span>
                    <span>
                      마지막: {new Date(selectedConversation.updated_at).toLocaleString('ko-KR')}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                대화를 선택하세요.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
